import { useMemo } from 'react'

import { JSBI, Pair, Percent } from '../../sdk'
import { useTotalSupply } from '../../data/TotalSupply'
import { useTokenBalance } from '../../state/wallet/hooks'
import { unwrappedToken } from '../../utils/wrappedCurrency'

export type LiquidityPositionMetrics = {
  loading: boolean
  poolShare: Percent | undefined
  lpBalance: string
  token0Amount: string
  token1Amount: string
  token0Symbol: string
  token1Symbol: string
  reserve0: string
  reserve1: string
  price: string
  pairAddress: string
}

export function useLiquidityPositionMetrics(
  pair: Pair | null,
  account?: string | null
): LiquidityPositionMetrics {
  const userPoolBalance = useTokenBalance(account ?? undefined, pair?.liquidityToken)
  const totalPoolTokens = useTotalSupply(pair?.liquidityToken)

  return useMemo(() => {
    if (!pair) {
      return {
        loading: true,
        poolShare: undefined,
        lpBalance: '—',
        token0Amount: '—',
        token1Amount: '—',
        token0Symbol: '—',
        token1Symbol: '—',
        reserve0: '—',
        reserve1: '—',
        price: '—',
        pairAddress: '—',
      }
    }

    const currency0 = unwrappedToken(pair.token0)
    const currency1 = unwrappedToken(pair.token1)

    const poolShare =
      userPoolBalance && totalPoolTokens && JSBI.greaterThan(totalPoolTokens.raw, JSBI.BigInt(0))
        ? new Percent(userPoolBalance.raw, totalPoolTokens.raw)
        : undefined

    const [token0Deposited, token1Deposited] =
      pair &&
      totalPoolTokens &&
      userPoolBalance &&
      JSBI.greaterThanOrEqual(totalPoolTokens.raw, userPoolBalance.raw)
        ? [
            pair.getLiquidityValue(pair.token0, totalPoolTokens, userPoolBalance, false),
            pair.getLiquidityValue(pair.token1, totalPoolTokens, userPoolBalance, false),
          ]
        : [undefined, undefined]

    let price = '—'
    try {
      if (pair.reserve0.greaterThan('0') && pair.reserve1.greaterThan('0')) {
        price = `${pair.token1Price.toSignificant(6)} ${currency1.symbol}`
      }
    } catch {
      /* ignore */
    }

    return {
      loading: false,
      poolShare,
      lpBalance: userPoolBalance?.toSignificant(6) ?? '0',
      token0Amount: token0Deposited?.toSignificant(6) ?? '0',
      token1Amount: token1Deposited?.toSignificant(6) ?? '0',
      token0Symbol: currency0.symbol ?? 'T0',
      token1Symbol: currency1.symbol ?? 'T1',
      reserve0: pair.reserve0.toSignificant(6),
      reserve1: pair.reserve1.toSignificant(6),
      price,
      pairAddress: pair.liquidityToken.address,
    }
  }, [pair, userPoolBalance, totalPoolTokens])
}
